"use client";
import { NextPage } from "next";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { RxHamburgerMenu } from "react-icons/rx";
import { HiOutlineXMark } from "react-icons/hi2";
import Dropdown from "./sub-components/Dropdown";
import DropdownMobile from "./sub-components/DropdownMobile";
import {
  CONTACT_API,
  PRODUCTS_API,
  REVALIDATE_DATA_NONE,
  SERVICES_API,
} from "@/properties/variables";
import { usePathname } from "next/navigation";
import useFetchOptions from "@/utils/hooks/useFetchOptions";
import christmasAnimation from "../../assets/gifs/christmas-animation.gif";

const accessoriesAvailOption = [
  "Zubehör-&-Anbauteile",
  "Zubehörteile-für-Radlader",
  "Weiteres-Zubehör-für-unsere-Maschinen",
];

const Navbar: NextPage = () => {
  const [hamburgerMenuOpen, setHamburgerMenuOpen] = useState(false);
  const pathname = usePathname();

  const toggleHamburgerMenu = () => {
    setHamburgerMenuOpen(!hamburgerMenuOpen);
  };

  // Fetch product categories
  const {
    availOptions: machineAvailOptions,
    loading: loadingMachines,
    error: errorMachines,
  } = useFetchOptions(PRODUCTS_API, "category");

  // Fetch contact locations
  const {
    availOptions: contactAvailOptions,
    loading: loadingContacts,
    error: errorContacts,
  } = useFetchOptions(CONTACT_API, "location");

  // Fetch services
  const {
    availOptions: servicesAvailOptions,
    loading: loadingServices,
    error: errorServices,
  } = useFetchOptions(SERVICES_API, "category");

  return (
    <nav>
      <div className="navbar-wrapper">
        <div className="logo">
          <Link href="/" className="logo-wrapper">
            <Image
              src="/akrotect-baumaschinen-logo.png"
              width={200}
              height={27}
              alt="akrotect-baumaschinen-logo"
              quality={100}
              priority
            />
          </Link>
        </div>

        <div className="nav-links">
          <ul className="main-links">
            <li
              className={`list-element ${pathname === "/" ? "navlink-active-desktop" : ""}`}
            >
              <Link href="/">startseite</Link>
            </li>

            {!loadingMachines && machineAvailOptions.length > 0 && (
              <Dropdown
                option="baumaschinen"
                availableFilters={machineAvailOptions}
                pathname={pathname}
                typeOfDropdown="categoryFilter"
              />
            )}

            <Dropdown
              option="zubehoer"
              availableFilters={accessoriesAvailOption}
              pathname={pathname}
              typeOfDropdown="inPage"
              dropdownName="Zubehör"
            />

            <li
              className={`list-element ${pathname === "/dienstleistungen" ? "navlink-active-desktop" : ""}`}
            >
              <Link href="/dienstleistungen">dienstleistungen</Link>
            </li>

            {!loadingContacts && contactAvailOptions.length > 0 && (
              <Dropdown
                option="kontakt"
                availableFilters={contactAvailOptions}
                pathname={pathname}
                typeOfDropdown="inPage"
              />
            )}
          </ul>
        </div>

        <div className="hamburger-menu-icon">
          <RxHamburgerMenu
            className="hamburger-menu-icon"
            size={32}
            onClick={toggleHamburgerMenu}
          />
        </div>
      </div>

      <div className={`hamburger-menu ${hamburgerMenuOpen ? "open" : ""}`}>
        <div className="hamburger-menu-wrapper">
          <div className="logo-icon-wrapper">
            <Image
              src="/akrotect-baumaschinen-logo.png"
              width={200}
              height={27}
              alt="akrotect-baumaschinen-logo"
              quality={100}
            />
            <HiOutlineXMark size={32} onClick={toggleHamburgerMenu} />
          </div>

          <div className="hamburger-menu-links">
            <ul className="hamburger-menu-main-links">
              <li
                className={`list-element ${pathname === "/" ? "navlink-mobile-active" : ""}`}
                onClick={toggleHamburgerMenu}
              >
                <Link href="/" className="link">
                  startseite
                </Link>
              </li>

              {!loadingMachines && machineAvailOptions.length > 0 && (
                <DropdownMobile
                  availableFilters={machineAvailOptions}
                  option="baumaschinen"
                  toggleDropdown={toggleHamburgerMenu}
                  pathname={pathname}
                  typeOfDropdown="categoryFilter"
                />
              )}

              <DropdownMobile
                availableFilters={accessoriesAvailOption}
                option="zubehoer"
                toggleDropdown={toggleHamburgerMenu}
                pathname={pathname}
                typeOfDropdown="inPage"
                dropdownName="Zubehör"
              />

              {!loadingServices && servicesAvailOptions.length > 0 && (
                <DropdownMobile
                  availableFilters={servicesAvailOptions}
                  option="dienstleistungen"
                  toggleDropdown={toggleHamburgerMenu}
                  pathname={pathname}
                  typeOfDropdown="toPage"
                />
              )}

              {!loadingContacts && contactAvailOptions.length > 0 && (
                <DropdownMobile
                  availableFilters={contactAvailOptions}
                  option="kontakt"
                  toggleDropdown={toggleHamburgerMenu}
                  pathname={pathname}
                  typeOfDropdown="inPage"
                />
              )}
            </ul>
          </div>

          <div className="custom-list-element" onClick={toggleHamburgerMenu}>
            <Link href="/">&#169; akrotect-Baumaschinen-GmbH</Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
